import { CONFIG } from '@/config/Config';
import axios, { AxiosResponse } from 'axios';
import { addSessionId } from '@/core/utils/httpHeaders';

// ─── Channel icon data (per-channel icons endpoint) ──────────────────────────

export interface ChannelIcons {
  default: string;
  negative: string;
  positive: string;
}

export interface ChannelIconData {
  icons: ChannelIcons;
}

const loadedChannels: Record<string, ChannelIconData> = {};

export async function fetchChannel(channel: string): Promise<ChannelIconData> {
  try {
    if (loadedChannels[channel]) {
      return loadedChannels[channel];
    }

    const headers = addSessionId({
      accept: 'application/json',
    });

    const response: AxiosResponse<ChannelIconData> = await axios.get(`${CONFIG.getChannelsUrl()}${channel}`, {
      headers,
    });
    loadedChannels[channel] = response.data;
    return response.data;
  } catch (error) {
    console.error('Error fetching channel:', error);
    throw error;
  }
}

// ─── Channel metadata (full channel list endpoint) ───────────────────────────

export interface ChannelLabels {
  pt: string;
  en: string;
  es: string;
  [key: string]: string;
}

export interface ChannelSupport {
  listening: boolean;
  setup: boolean;
  connections: boolean;
  benchmarks: boolean;
  smartcare: boolean;
  radar: boolean;
  ignore_sentiment: boolean;
  credit_system: boolean;
  sac_types: string[] | null;
}

export interface Channel {
  key: string;
  labels: ChannelLabels;
  aliases?: string[];
  feature_flag?: string;
  support?: ChannelSupport;
}

export type ListeningChannelOption = Pick<Channel, 'key' | 'labels'>;

let channelsCache: Channel[] | null = null;
let channelsFetchPromise: Promise<Channel[]> | null = null;

export async function getChannels(): Promise<Channel[]> {
  if (channelsCache) return channelsCache;
  if (channelsFetchPromise) return channelsFetchPromise;
  channelsFetchPromise = (async () => {
    try {
      const headers = addSessionId({ accept: 'application/json' });
      const response = await axios.get<Channel[]>(CONFIG.getChannelsUrl(), { headers });
      channelsCache = response.data;
      return channelsCache;
    } catch (error) {
      console.error('Error fetching channels:', error);
      throw error;
    } finally {
      channelsFetchPromise = null;
    }
  })();
  return channelsFetchPromise;
}

let cachedListeningChannels: ListeningChannelOption[] | null = null;

export async function fetchListeningChannels(): Promise<ListeningChannelOption[]> {
  try {
    if (cachedListeningChannels) {
      return cachedListeningChannels;
    }

    const allChannels = await getChannels();

    cachedListeningChannels = allChannels
      .filter((channel) => channel.support?.listening === true)
      .map((channel) => ({
        key: channel.key,
        labels: channel.labels,
      }));

    return cachedListeningChannels;
  } catch (error) {
    console.error('Error fetching listening channels:', error);
    throw error;
  }
}